const SESSION_CONTEXT_KEY = "api_platform_analytics_session_context";

const PRODUCT = "api_platform";

const AI_HOSTS = [
  "chatgpt.com",
  "chat.openai.com",
  "claude.ai",
  "perplexity.ai",
  "gemini.google.com",
  "copilot.microsoft.com",
  "poe.com",
  "you.com",
];

const SEARCH_HOSTS = [
  "google.",
  "bing.com",
  "yahoo.",
  "duckduckgo.com",
  "baidu.com",
  "yandex.",
  "ecosia.org",
];

const SOCIAL_HOSTS = [
  "linkedin.com",
  "facebook.com",
  "instagram.com",
  "reddit.com",
  "x.com",
  "t.co",
  "tiktok.com",
  "youtube.com",
];

const PAID_SEARCH_MEDIUMS = new Set([
  "cpc",
  "ppc",
  "paidsearch",
  "paid_search",
  "sem",
]);

const PAID_SOCIAL_MEDIUMS = new Set([
  "paid_social",
  "paidsocial",
  "social_paid",
  "social-ads",
]);

const ORGANIC_SOCIAL_MEDIUMS = new Set([
  "social",
  "organic_social",
  "organicsocial",
]);

const DISPLAY_MEDIUMS = new Set(["display", "banner", "cpm", "programmatic"]);

const EMAIL_MEDIUMS = new Set(["email", "edm", "newsletter"]);
const AFFILIATE_MEDIUMS = new Set(["affiliate", "affiliates"]);

const includesHost = (hostname, hosts) =>
  hosts.some(
    (host) =>
      hostname === host ||
      hostname.endsWith(`.${host}`) ||
      hostname.includes(host)
  );

const getHostname = (value) => {
  if (!value) return "";

  try {
    return new URL(value).hostname.toLowerCase();
  } catch {
    return "";
  }
};

const getLandingPage = (pathname) => {
  if (pathname.startsWith("/docs")) return "docs";

  switch (pathname) {
    case "/":
      return "capabilities";
    case "/pricing":
      return "pricing";
    case "/documentation":
      return "docs";
    case "/apiKeys":
      return "API_keys";
    case "/billing":
      return "billing";
    default:
      return "unknown";
  }
};

const getTrafficSource = (url, referrer) => {
  const medium = (url.searchParams.get("utm_medium") || "").toLowerCase();
  const source = (
    url.searchParams.get("utm_source") ||
    url.searchParams.get("source") ||
    ""
  ).toLowerCase();
  const referrerHost = getHostname(referrer);

  if (PAID_SEARCH_MEDIUMS.has(medium)) return "search_paid";
  if (PAID_SOCIAL_MEDIUMS.has(medium)) return "paid_social";
  if (ORGANIC_SOCIAL_MEDIUMS.has(medium)) return "organic_social";
  if (DISPLAY_MEDIUMS.has(medium)) return "display_ads";
  if (EMAIL_MEDIUMS.has(medium)) return "email";
  if (AFFILIATE_MEDIUMS.has(medium)) return "affiliate";

  if (
    url.searchParams.has("gclid") ||
    url.searchParams.has("gbraid") ||
    url.searchParams.has("wbraid") ||
    url.searchParams.has("msclkid") ||
    url.searchParams.has("bd_vid")
  ) {
    return "search_paid";
  }

  if (
    url.searchParams.has("fbclid") ||
    url.searchParams.has("ttclid") ||
    url.searchParams.has("li_fat_id")
  ) {
    return "paid_social";
  }

  if (AI_HOSTS.some((host) => source.includes(host.split(".")[0]))) {
    return "genai";
  }
  if (SEARCH_HOSTS.some((host) => source.includes(host.split(".")[0]))) {
    return "search_organic";
  }
  if (SOCIAL_HOSTS.some((host) => source.includes(host.split(".")[0]))) {
    return "organic_social";
  }

  const sourceHost = getHostname(source);
  const acquisitionHost = sourceHost || referrerHost;

  if (includesHost(acquisitionHost, AI_HOSTS)) return "genai";
  if (includesHost(acquisitionHost, SEARCH_HOSTS)) return "search_organic";
  if (includesHost(acquisitionHost, SOCIAL_HOSTS)) return "organic_social";

  if (referrerHost) return "referrals";
  if (!source && !medium) return "direct";

  return "unknown";
};

const createSessionContext = () => {
  const url = new URL(window.location.href);

  return {
    product: PRODUCT,
    traffic_source: getTrafficSource(url, document.referrer),
    landing_page: getLandingPage(url.pathname),
  };
};

export const getAnalyticsSessionContext = () => {
  if (typeof window === "undefined") {
    return {
      product: PRODUCT,
      traffic_source: "unknown",
      landing_page: "unknown",
    };
  }

  try {
    const storedContext = window.sessionStorage.getItem(SESSION_CONTEXT_KEY);
    if (storedContext) {
      return JSON.parse(storedContext);
    }

    const context = createSessionContext();
    window.sessionStorage.setItem(SESSION_CONTEXT_KEY, JSON.stringify(context));
    return context;
  } catch {
    return createSessionContext();
  }
};

export const getPaymentAttributionContext = () => {
  const { traffic_source, landing_page } = getAnalyticsSessionContext();

  return {
    traffic_source,
    landing_page,
    product: PRODUCT,
  };
};
